/**
 * Translations remembered one sentence at a time.
 *
 * The whole-answer cache (lib/resultCache.ts) answers "have I translated
 * exactly this box before?", which is the right question for a paste and the
 * wrong one for typing. Someone composing a paragraph changes the box on every
 * keystroke, so that cache misses every time, and the model was handed the
 * entire paragraph again to produce one new sentence at the end of it. Three
 * sentences in, a pause cost a re-translation of all three: paid for, generated
 * again, and swapped onto the screen in place of an answer that was already
 * correct and was being read.
 *
 * This asks the smaller question — "have I translated THIS SENTENCE before?" —
 * and it hits constantly while someone writes, because the sentences they
 * finished ten seconds ago have not changed. What is left over is the new one,
 * and that is all that gets asked for.
 *
 * Two things it deliberately does not do:
 *
 *   • It does not translate a sentence out of context. The request still
 *     carries the whole text; only the ANSWER is trimmed to the missing lines.
 *     See ONLY_MARK in the backend's lib/modes.ts for why that distinction is
 *     the entire point.
 *   • It does not apply to every mode. A sentence of a Humanize rewrite or a
 *     Mistakes report is not a function of that sentence alone, so holding one
 *     would be holding something that was never true. The `incremental` flag on
 *     the mode decides, and it is set for faithful translation only.
 *
 * `sessionStorage`, like the answer cache: a scratch buffer for this tab, not a
 * store that should follow anyone into next week.
 */

const KEY = 'gt.sentences.v1';
/**
 * Sentences are small, and the whole value of this is depth — a long document
 * edited over twenty minutes should still be finding its earlier paragraphs in
 * here. 600 short sentences is a few tens of kilobytes.
 */
const MAX = 600;
/** A "sentence" longer than this is a paste with no punctuation; not our case. */
const MAX_CHARS = 600;

/**
 * One question. The language pair and the mode are part of it — the same
 * sentence has a different answer in each — and `auto` is kept distinct from the
 * language it resolves to, because the prompt genuinely differs (see the note on
 * the cache key in the backend's routes/translate.ts).
 *
 * NFC for the reason the other caches do it: two encodings of `안` look
 * identical and compare unequal.
 */
function keyFor(from: string, to: string, mode: string, sentence: string): string {
  return `${from}|${to}|${mode}|${sentence.normalize('NFC').trim()}`;
}

let memory = new Map<string, string>();
/** Which model produced these, so a model change throws them away. */
let model: string | null = null;
let loaded = false;

function load(): void {
  if (loaded) return;
  loaded = true;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.entries)) return;
    model = typeof parsed.model === 'string' ? parsed.model : null;
    memory = new Map(
      parsed.entries.filter(
        (e: unknown): e is [string, string] =>
          Array.isArray(e) && e.length === 2 && typeof e[0] === 'string' && typeof e[1] === 'string',
      ),
    );
  } catch {
    // A half-written value or an older schema is not worth a broken page.
    memory = new Map();
  }
}

let timer: ReturnType<typeof setTimeout> | null = null;

function save(): void {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    try {
      sessionStorage.setItem(KEY, JSON.stringify({ model, entries: [...memory].slice(-MAX) }));
    } catch {
      try { sessionStorage.removeItem(KEY); } catch { /* nothing left to try */ }
    }
  }, 1000);
}

/**
 * File the pairs a finished translation produced.
 *
 * Called with the links `reshape` handed back, which only exist when the answer
 * really did correspond to the source piece for piece — so every pair stored
 * here is one the app has already shown on screen as a matched pair, not one it
 * inferred.
 */
export function remember(
  from: string, to: string, mode: string,
  pairs: { src: string; out: string }[],
  answeredBy?: string,
): void {
  load();
  if (answeredBy) {
    // The admin can change the translation model from the console. Sentences
    // from the previous one would otherwise be spliced into answers from the
    // new one, giving a paragraph written by two different models.
    if (model && model !== answeredBy) memory.clear();
    model = answeredBy;
  }
  for (const { src, out } of pairs) {
    if (!src.trim() || !out.trim()) continue;
    if (src.length > MAX_CHARS || out.length > MAX_CHARS) continue;
    const k = keyFor(from, to, mode, src);
    memory.delete(k);          // re-insert so recency is the map's own order
    memory.set(k, out);
  }
  while (memory.size > MAX) memory.delete(memory.keys().next().value as string);
  save();
}

/**
 * What we already hold for each of these sentences, in order.
 *
 * `null` marks the ones that have to be asked for. The array is the same length
 * as the input, so the caller can splice the answer straight back into it
 * without tracking indices separately — which is where an off-by-one would put
 * the wrong translation under the wrong sentence, silently.
 */
export function lookup(
  from: string, to: string, mode: string, sentences: string[],
): (string | null)[] {
  load();
  return sentences.map((s) => memory.get(keyFor(from, to, mode, s)) ?? null);
}

/** Test seam and a way out if a session ever holds something wrong. */
export function forget(): void {
  load();
  memory.clear();
  save();
}
